import React, {useEffect} from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import {
  Button, Table,
  Tag, Typography, notification, Icon,
} from 'antd';
import { connect } from 'react-redux';
import ConfigHeading from '../components/ConfigHeading';
import { selectUsersRequest } from '../../../../state/ui/configuration/users/userUiReducer';
import { selectUsers } from '../../../../state/domain/users/userReducer';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import { editUser as editUserAction, requestUsers, requestDeleteUser } from '../../../../state/ui/configuration/users/userUiActions';
import { requestSendVerificationEmail } from '../../../../state/ui/configuration/users/emailUiActions';
import EditUser from './EditUser';
import {
  filterStatus,
  userRoles,
  userStatuses,
  getStatus,
  roleNames,
  userIsAdmin,
  userStatusNames,
  userIsConsoleAdmin,
  userIsCompanyAdmin, userHasCompanyRolesButNotAdmin,
} from './user-util';
import ConfirmButton from '../../../../components/buttons/ConfirmButton';
import {userIsAdminConsole} from "../../../../utility/security/security-util";

const { Column } = Table;
const { Text } = Typography;

// TABLE
function UsersTable({
  isLoading, users, onEditButton, sessionUser, onDeleteButton,
}) {
  const RowTotal = () => (
    <div>
      <Text>{`${users.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  // User Filter Roles to match logged-in user
  const showingRoles = userRoles.filter(userRole => {
    if (!userIsAdminConsole(sessionUser)) {
      return userRole.roleName === roleNames.ADMIN || userRole.roleName === roleNames.INSPECTOR;
    } else {
       if (userIsCompanyAdmin(sessionUser)) {
         return userRole.roleName === roleNames.COMPANY_ADMIN || userRole.roleName === roleNames.COMPANY_FINANCE;
       } else if (userIsConsoleAdmin(sessionUser)) {
         return userRole.roleName === roleNames.CONSOLE_ADMIN || userRole.roleName === roleNames.CONSOLE_FINANCE
             || userRole.roleName === roleNames.COMPANY_ADMIN || userRole.roleName === roleNames.COMPANY_FINANCE;   //what can see
       } else {
         // no admin roles = no filter (users not shown anyway)
         return false;
       }
    }
  });

  // EXTRA FILTERS
  function filterRoles(value, record) {
    return !!record.linkedRoles.find(linkedRole => linkedRole === value);
  }
  function filterLocations(value, record) {
    return record.locationName === value;
  }

  // SORTS
  const sortText = (a, b) => {
    const aStr = a == null ? '' : a.toString();
    const bStr = b == null ? '' : b.toString();
    return aStr.localeCompare(bStr);
  };

  function sortUsersByDisplayName(a, b) {
    const aStr = `${a.displayName}`;
    const bStr = `${b.displayName}`;
    return aStr.localeCompare(bStr);
  }

  function sortUsersByUsername(a, b) {
    const aStr = `${a.username}`;
    const bStr = `${b.username}`;
    return aStr.localeCompare(bStr);
  }
  // TABLE SETTINGS, EXPANDED ROW, COLUMNS
  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={users}
      rowKey={user => user.username}
    >
      <Column
        title={<FormattedMessage id="status" />}
        with={150}
        dataIndex="status"
        key="status"
        align="center"
        filtered
        filters={userStatuses.map(userStatus => ({
          text: <FormattedMessage id={userStatus.statusTranslationId} />,
          value: userStatus.status,
        }))}
        filteredValue={users.active}
        onFilter={filterStatus}
        render={(active, record) => {
          const recordStatus = getStatus(record);
          return (
            <Tag color={recordStatus.color} key={recordStatus.status}>
              {<FormattedMessage id={userStatuses.find(userStatus => userStatus.status === recordStatus.status).statusTranslationId} />}
            </Tag>
          );
        }}
      />
      <Column
        sorter={sortUsersByDisplayName}
        defaultSortOrder="ascend"
        title={<FormattedMessage id="displayName" />}
        dataIndex="displayName"
        key="displayName"
        render={(active, record) => {
          return (
              <div><Text>{record.displayName}</Text>{ record.userId === sessionUser.userId ? <span>&nbsp;&nbsp;<Icon type="user" /></span> : <></>}</div>
          );
        }}
      />
      { !userIsAdminConsole(sessionUser)
          ? (
          <Column
              title={<FormattedMessage id="currentLocation" />}
              dataIndex="locationName"
              key="locationName"
              align="center"
              filters={
                users
                    // eslint-disable-next-line max-len
                    ? [...new Set(users.map(row => row.locationName ? row.locationName : ''))].sort((a, b) => sortText(a, b)).map(desc => ({ text: desc ? desc : '', value: desc ? desc : '' }))
                    : ''
              }
              filteredValue={users.locationName}
              onFilter={filterLocations}
          />
      ) : ''}
      <Column
        sorter={sortUsersByUsername}
        title={<FormattedMessage id="email" />}
        dataIndex="username"
        key="username"
      />
      <Column
        title={<FormattedMessage id="roles" />}
        dataIndex="linkedRoles"
        key="linkedRoles"
        filtered
        filters={showingRoles.map(userRole => ({
          text: <FormattedMessage id={userRole.roleTranslationId} />,
          value: userRole.roleName,
        }))}
        onFilter={filterRoles}
        align="center"
        render={(linkedRoles) => {
          // keep the order of the roles consistent here. Only really matters if a user has been created or updated
          // as the order can be varied in that process
          const orderedRoles = linkedRoles.slice().sort();
          orderedRoles.reverse();
          return (
            <span>
              {
                orderedRoles.map((linkedRole) => {
                  let color = 'purple';
                  if (linkedRole === roleNames.INSPECTOR) {
                    color = 'blue';
                  } else if (linkedRole === roleNames.ADMIN) {
                    color = 'purple';
                  } else if (linkedRole === roleNames.CONSOLE_FINANCE) {
                    color = 'magenta';
                  } else if (linkedRole === roleNames.CONSOLE_ADMIN) {
                    color = 'geekblue';
                  } else if (linkedRole === roleNames.COMPANY_FINANCE) {
                    color = 'purple';
                  } else if (linkedRole === roleNames.COMPANY_ADMIN) {
                    color = 'blue';
                  } else if (linkedRole === roleNames.READ_ONLY) {
                    color = 'lime'; //not shown but in case
                  }
                  const descriptionId = userRoles.find(userRole => userRole.roleName === linkedRole)
                    ? userRoles.find(userRole => userRole.roleName === linkedRole).roleTranslationId
                    : 'error';
                  // don't display read only
                  return linkedRole !== roleNames.READ_ONLY ? (
                    <Tag color={color} key={linkedRole}>
                      {<FormattedMessage id={descriptionId} />}
                    </Tag>
                  ) : null;
                })
                }
            </span>
          );
        }}
      />
      { userIsAdmin(sessionUser) || userIsConsoleAdmin(sessionUser) || userIsCompanyAdmin(sessionUser)
        ? (
          <Column
            className="via-action-column"
            align="center"
            width={150}
            render={(text, record) => (
              <div className="button-flex-col">
                {getStatus(record).status === userStatusNames.INVITED && (
                  <ConfirmButton size="small" type="danger" onClick={() => onDeleteButton(record.userId, record.username)} icon="stop">
                    <FormattedMessage id="delete" />
                  </ConfirmButton>
                )}
                {!(userHasCompanyRolesButNotAdmin(record) && userIsConsoleAdmin(sessionUser)) && (  //no edit if admin logged in and user has a non-admin company role, finance at this stage
                  <Button size="small" type="primary" onClick={() => onEditButton(record)} icon="edit">
                    <FormattedMessage id="edit" />
                  </Button>
                )}
              </div>
            )}
          />
        ) : ''}
    </Table>
  );
}

UsersTable.propTypes = {
  isLoading: PropTypes.bool,
  onEditButton: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      username: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      emailVerified: PropTypes.bool.isRequired,
      linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
      locationName: PropTypes.string,
      active: PropTypes.bool.isRequired,
    }),
  ),
  sessionUser: PropTypes.shape({
    username: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    emailVerified: PropTypes.bool.isRequired,
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
    active: PropTypes.bool.isRequired,
  }).isRequired,
  onDeleteButton: PropTypes.func.isRequired,
};

UsersTable.defaultProps = {
  isLoading: false,
  users: [],
};

function ManageUsers({
  isLoading, loadPageData, users, deleteInvitedUser,
  editUser, sessionUser, sendNewVerificationEmail,
}) {
  // Load users on first entry - these only need to be loaded once.
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);
  const intl = useIntl();

  // Extra validation helper
  const doesUsernameExist = (userId, value) => {
    const matchedUser = users.find(row => row.username.toString().toLowerCase() === (value || '').toString().toLowerCase());
    return matchedUser ? matchedUser.userId !== userId : false;
  };

  const unVerifiedNotificationDescription = (
    <div>
      <Text>{intl.formatMessage({ id: 'notVerifiedDesc' })}</Text>
      <Button
        style={{
          marginTop: '12px',
        }}
        type="primary"
        block
        onClick={() => { notification.close('resend-verification-email'); sendNewVerificationEmail(); }}
      >
        {intl.formatMessage({ id: 'verifyEmailResend' })}
      </Button>
    </div>
  );

  const onCreateOrEdit = (user) => {
    if (!sessionUser.linkedRoles.find(linkedRole => linkedRole === roleNames.ADMIN || linkedRole === roleNames.CONSOLE_ADMIN || linkedRole === roleNames.COMPANY_ADMIN )) {
      notification.warn({
        message: intl.formatMessage({ id: 'insufficientPermissions' }),
        description: intl.formatMessage({ id: 'notAdministrator' }),
        duration: null,
      });
    } else if (!sessionUser.emailVerified) {
      notification.warn({
        key: 'resend-verification-email',
        message: intl.formatMessage({ id: 'notVerified' }),
        description: unVerifiedNotificationDescription,
        duration: null,
      });
    } else {
      editUser(user);
    }
  };

  const isLastAdmin = (username) => {
    const adminUsers = users.filter(user => !!user.linkedRoles.find(role => role === roleNames.ADMIN));
    return adminUsers.length === 1 && adminUsers[0].username === username;
  };

  const isLastCompanyAdmin = (username) => {
    const coAdminUsers = users.filter(user => !!user.linkedRoles.find(role => role === roleNames.COMPANY_ADMIN));
    return coAdminUsers.length === 1 && coAdminUsers[0].username === username;
  };

  const CreateButton = () => (
    <Button type="primary" onClick={() => onCreateOrEdit(null)} loading={isLoading} icon="plus">
      <FormattedMessage id="create" />
    </Button>
  );

  return (
    <ConfigHeading
      titleId={userIsAdminConsole(sessionUser) ? 'identityAdminConsole' : 'identity'}
      descriptionId="manageUsersFunction"
      actions={userIsAdmin(sessionUser) || userIsConsoleAdmin(sessionUser) || userIsCompanyAdmin(sessionUser) ? <CreateButton /> : ''}
    >
      <UsersTable
        isLoading={isLoading}
        users={users}
        onEditButton={onCreateOrEdit}
        sessionUser={sessionUser}
        onDeleteButton={deleteInvitedUser}
      />
      <EditUser usernameValidator={doesUsernameExist} isLastAdmin={isLastAdmin} isLastCompanyAdmin={isLastCompanyAdmin} />
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
ManageUsers.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  editUser: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      username: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      emailVerified: PropTypes.bool.isRequired,
      linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
      locationId: PropTypes.number,
      locationName: PropTypes.string,
      active: PropTypes.bool.isRequired,
    }),
  ),
  sessionUser: PropTypes.shape({
    username: PropTypes.string.isRequired,
    emailVerified: PropTypes.bool.isRequired,
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  sendNewVerificationEmail: PropTypes.func.isRequired,
  deleteInvitedUser: PropTypes.func.isRequired,
};

ManageUsers.defaultProps = {
  isLoading: false,
  users: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectUsersRequest(state);
  const allUsers = selectUsers(state);

  return {
    sessionUser: selectSessionUser(state),
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    users: allUsers,
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: (isCompanyAdmin) => dispatch(requestUsers(isCompanyAdmin)),
  // eslint-disable-next-line max-len
  editUser: user => dispatch(editUserAction(user ? user.userId : null)),
  sendNewVerificationEmail: () => dispatch(requestSendVerificationEmail()),
  deleteInvitedUser: (userId, username) => dispatch(requestDeleteUser(userId, username)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(ManageUsers));
